import React, { useEffect, useState, useMemo, useRef } from 'react';
import { Helmet } from 'react-helmet';
import { useViewlogies } from 'context';
import { refresh, scheduleAtTime, useHashFragment } from 'utils';
import { isAuthenticated } from 'services/viewlogyAuth';
import Header from 'components/ClientHeader';
import ContactUs from 'components/ContactUs';
import Footer from 'components/Footer';
import FuneralStream from './FuneralStream';
import Navigation from './Navigation';
import MobileNavigation from './MobileNavigation';
import MobileFooter from './MobileFooter';
import GuestSection from './GuestSection';
import getActiveStreamIndex from './getActiveStreamIndex';
import PlanningResource from './PlanningResource';
import GriefResource from './GriefResource';
import CharacterCloud from './CharacterCloud';
import { USE_MOCK_DATA } from './CharacterCloud/config';
import CarePackage from './CarePackage';
import RegistrationModal from './Registration';
import { getRegistrationCookie, setRegistrationCookie } from 'services/guest';
import sharedMessages from 'messages';
import './viewlogy.scss';
import Donations from 'components/Donations';

const Viewlogy = props => {
    const {
        event,
        event: {
            firstName,
            lastName,
            isCharacterCloud: eventIsCharacterCloud,
            isCarePackage: eventIsCarePackage,
            memorialMomentsUrl,
        },
        videoLogin,
        streams,
        setIsLoggedIn,
        setIsPinLoggedIn,
        setStreams,
        setStreamIndex,
        isMobile,
        isShortMobile,
        fetchCharacterCloud,
        isPinLoggedIn,
    } = useViewlogies();

    // Use isCharacterCloud from API, default to false if not present
    const isCharacterCloud = eventIsCharacterCloud === true;

    // Use isCarePackage from API, default to false if not present
    const isCarePackage = eventIsCarePackage === true;

    const title = `${firstName} ${lastName} — ${sharedMessages.viewlogiesAllCaps}`;

    const eventStarted = new Date(streams[0].startTime).getTime() < new Date().getTime();

    const isSpecialEvent = streams.every(stream => stream.name === 'Special Event');

    const [activeTab, setActiveTab] = useState('stream');

    const [topbarHeight, setTopbarHeight] = useState(0);

    const [registrationComplete, setRegistrationComplete] = useState(() => !!getRegistrationCookie(event.id));

    const handleRegistrationComplete = () => {
        setRegistrationCookie(event.id);
        setRegistrationComplete(true);
    };

    const tabs = useMemo(
        () => [
            'stream',
            'guestList',
            ...(isCharacterCloud ? ['characterCloud'] : []),
            ...(isCarePackage ? ['carePackages'] : []),
            ...(!isSpecialEvent ? ['funeralGuide', 'griefSupport'] : []),
            ...(isMobile ? ['helpCenter'] : []),
        ],
        [isSpecialEvent, isMobile, isCharacterCloud, isCarePackage],
    );

    const mobileScrollTab = (tab, extraOffset = 0) => {
        const element = document.getElementById(tab);
        if (element) {
            const topbarOffset = topbarHeight - 1 || 0;
            const y = element.getBoundingClientRect().top + window.pageYOffset - topbarOffset + extraOffset;
            window.scrollTo({ top: y, behavior: 'smooth' });
        }
    };

    const browserTabSelect = tab => {
        setActiveTab(tab);
        window.history.replaceState(null, '', `#${tab}`);
    };

    const handleScroll = () => {
        // const sections = document.querySelectorAll<HTMLElement>('[data-tab]');
        const sections = document.querySelectorAll('[data-tab]');
        const scrollY = window.scrollY + topbarHeight;

        for (const section of sections) {
            const rect = section.getBoundingClientRect();
            const top = rect.top + window.scrollY;
            const bottom = top + section.offsetHeight;

            if (scrollY >= top && scrollY < bottom) {
                const tab = section.getAttribute('data-tab');
                if (tab && tab !== activeTab) {
                    setActiveTab(tab);
                    window.history.replaceState(null, '', `#${tab}`);
                }
                break; // No need to check further
            }
        }
    };

    const getInitialHashTab = (validTabs = tabs) => {
        const hash = window.location.hash.replace('#', '');
        return validTabs.includes(hash) ? hash : null;
    };

    const updateTabFromHash = () => {
        const initialTab = getInitialHashTab(tabs);
        if (initialTab) {
            setActiveTab(initialTab);
        }
    };

    // useHashFragment();

    //on page load, uses url hash to set active page
    useEffect(() => {
        if (isMobile) {
            const initialTab = getInitialHashTab();
            mobileScrollTab(initialTab, 250);
        } else {
            updateTabFromHash(); // run on mount
            window.addEventListener('hashchange', updateTabFromHash);
            return () => window.removeEventListener('hashchange', updateTabFromHash);
        }
    }, [isMobile, tabs, topbarHeight]);

    // sets activeTab to current section on scroll
    useEffect(() => {
        window.addEventListener('scroll', handleScroll, { passive: true });

        handleScroll();

        return () => window.removeEventListener('scroll', handleScroll);
    }, [activeTab, topbarHeight]);

    /**
     * 1 - GA tracking
     * 2 - Video authentication
     * 3 - Set currently visible video
     */
    useEffect(() => {
        // set title manually before tracking page view
        document.title = `${firstName} ${lastName} — ${sharedMessages.viewlogiesAllCaps}`;

        if (props.pin) {
            videoLogin(props.pin); //triggers if pin is present in URL
        } else if (event.globalPin) {
            // check if authenticated
            const { globalPin: pin, id } = event;
            event.isLoggedIn = isAuthenticated({ pin, id, firstName });
            setIsLoggedIn(event.isLoggedIn);
            if (event.isLoggedIn) setIsPinLoggedIn(true);
        } else {
            setStreams(
                streams.map(stream => {
                    const { pin, id } = stream;
                    stream.isLoggedIn = isAuthenticated({ pin, id, firstName });
                    if (stream.isLoggedIn) setIsPinLoggedIn(true);
                    return stream;
                }),
            );
        }

        // show the tab for the current event: the first stream whose end time
        // hasn't passed, or the first tab once all streams have ended
        setStreamIndex(getActiveStreamIndex(streams, !!memorialMomentsUrl));
    }, []);

    /**
     * schedule automatic refreshes at stream start times, so the player picks
     * up the live video (the right tab is re-selected on reload)
     */
    useEffect(() => {
        // schedule a refresh for each stream the viewer is logged in to
        // (a global PIN logs in to all streams at once)
        const cancels = streams
            .filter(stream => (event.globalPin && event.isLoggedIn) || stream.isLoggedIn)
            .map(stream => scheduleAtTime(stream.startTime, [refresh]));
        return () => cancels.forEach(cancel => cancel());
    }, [event.isLoggedIn, streams]);

    /**
     * auto-advance the stream tab: at each stream's end time, switch to the
     * tab of the next stream that hasn't ended (skipping the Slideshow tab);
     * after the final stream ends, return to the first tab (Memorial Moments
     * when the event has one)
     */
    useEffect(() => {
        const cancels = streams
            .filter(stream => stream.id !== 'TRIBUTE_VIDEO')
            .map(stream =>
                scheduleAtTime(stream.endTime, [
                    () => setStreamIndex(getActiveStreamIndex(streams, !!memorialMomentsUrl)),
                ]),
            );
        return () => cancels.forEach(cancel => cancel());
    }, [streams, memorialMomentsUrl]);

    // Auto-refresh character cloud data (similar to guest list)
    useEffect(() => {
        // Skip fetching if using mock data
        if (USE_MOCK_DATA) {
            return;
        }
        if (isCharacterCloud && event.id && event.organizationId) {
            fetchCharacterCloud();
            const interval = setInterval(() => {
                fetchCharacterCloud();
            }, 30000); // Refresh every 30 seconds
            return () => clearInterval(interval);
        }
    }, [isCharacterCloud, event.id, event.organizationId, fetchCharacterCloud]);

    return (
        <>
            <Helmet>
                <title>{title}</title>
                <meta property="og:title" content={title} />
            </Helmet>
            <main className="main-content centered viewlogy">
                {!isMobile ? (
                    <div className="viewlogy-layout">
                        <Navigation tabs={tabs} activeTab={activeTab} onTabSelect={tab => browserTabSelect(tab)} />
                        {/* TAB */}
                        <div className="tab-content">
                            {activeTab === 'stream' && <FuneralStream />}
                            {activeTab === 'guestList' && <GuestSection />}
                            {activeTab === 'characterCloud' && <CharacterCloud />}
                            {activeTab === 'carePackages' && <CarePackage />}
                            {activeTab === 'funeralGuide' && <PlanningResource />}
                            {activeTab === 'griefSupport' && <GriefResource />}
                            {activeTab === 'helpCenter' && (
                                <div className="contact-us-wrapper">
                                    <ContactUs />
                                    {/* <div>** donations here</div> */}
                                    <Donations />
                                </div>
                            )}
                        </div>
                    </div>
                ) : isShortMobile ? (
                    <div className="viewlogy-mobile-layout">
                        <div id="stream" data-tab="stream">
                            <FuneralStream />
                        </div>
                        <div id="guestList" data-tab="guestList">
                            <GuestSection />
                        </div>
                        {isCharacterCloud && (
                            <div id="characterCloud" data-tab="characterCloud">
                                <CharacterCloud />
                            </div>
                        )}
                        {isCarePackage && (
                            <div id="carePackages" data-tab="carePackages">
                                <CarePackage />
                            </div>
                        )}
                        {/* <div id="funeralGuide" data-tab="funeralGuide">
                            <PlanningResource />
                        </div> */}
                        {/* <div id="griefSupport" data-tab="griefSupport">
                            <GriefResource />
                        </div> */}
                        <div id="helpCenter" data-tab="helpCenter">
                            <ContactUs />
                        </div>
                        {/* <div className="mobile-donation-container">
                            <Donations />
                        </div> */}
                    </div>
                ) : (
                    <>
                        <MobileNavigation
                            tabs={tabs}
                            activeTab={activeTab}
                            onTabSelect={tab => mobileScrollTab(tab)}
                            setTopbarHeight={setTopbarHeight}
                        />
                        <div style={{ height: topbarHeight }} />
                        <div className="viewlogy-mobile-layout">
                            <div id="stream" data-tab="stream">
                                <FuneralStream />
                            </div>
                            <div id="guestList" data-tab="guestList">
                                <GuestSection />
                            </div>
                            {isCharacterCloud && (
                                <div id="characterCloud" data-tab="characterCloud">
                                    <CharacterCloud />
                                </div>
                            )}
                            {isCarePackage && (
                                <div id="carePackages" data-tab="carePackages">
                                    <CarePackage />
                                </div>
                            )}
                            <div id="funeralGuide" data-tab="funeralGuide">
                                <PlanningResource />
                            </div>
                            <div id="griefSupport" data-tab="griefSupport">
                                <GriefResource />
                            </div>
                            <div id="helpCenter" data-tab="helpCenter">
                                <ContactUs />
                            </div>
                            <div className="mobile-donation-container">
                                <Donations />
                            </div>
                        </div>
                        <MobileFooter />
                    </>
                )}
            </main>
            {isPinLoggedIn && !registrationComplete && <RegistrationModal onComplete={handleRegistrationComplete} />}
        </>
    );
};

export default Viewlogy;
